import React from 'react';
import { Link } from 'react-router-dom';

const HelpCenter = () => (
  <div className="help-center">
    <header className="hc-hero">
      <h1>Help Center</h1>
      <p>Everything you need to know about TopLeadFinder—organized and easy to find.</p>
      <input
        className="hc-search"
        type="search"
        placeholder="Search the help center..."
        onChange={e => {
          // you could wire up filtering here
        }}
      />
    </header>

    <nav className="hc-nav">
      <ul>
        <li><a href="#getting-started">Getting Started</a></li>
        <li><a href="#account-profile">Account & Profile</a></li>
        <li><a href="#lead-generation">Lead Generation</a></li>
        <li><a href="#billing">Invoices & Billing</a></li>
        <li><a href="#security">Security & MFA</a></li>
        <li><a href="#faqs">Troubleshooting & FAQs</a></li>
        <li><Link to="/contact-support">Contact Support</Link></li>
      </ul>
    </nav>

    <section id="getting-started" className="hc-section">
      <h2>Getting Started</h2>
      <details>
        <summary>How do I create my free trial account?</summary>
        <ol>
          <li>Click “Start Free Trial” on the Pricing page or landing page.</li>
          <li>Enter your email and choose a password.</li>
          <li>Verify your email when you receive the confirmation link.</li>
          <li>Log in and start exploring your 5 free credits.</li>
        </ol>
      </details>

      <details>
        <summary>What are credits?</summary>
        <p>
          Credits are the quota you spend to generate leads. On Trial you get 5 credits; higher plans give you more per month/year.
        </p>
      </details>
    </section>

    <section id="account-profile" className="hc-section">
      <h2>Account & Profile</h2>
      <details>
        <summary>How do I update my name or email?</summary>
        <p>
          Go to Account → Profile. Your email is fixed (used for login), but you can edit your display name and save.
        </p>
      </details>
      <details>
        <summary>How do I reset my password?</summary>
        <p>
          On the Login page click “Forgot Password?”, enter your email, and follow the reset link in your inbox.
        </p>
      </details>
      <details>
        <summary>How do I enable or disable 2-Factor (MFA)?</summary>
        <p>
          In Account → Security click “Enable 2-Factor Auth” to scan the QR code, then enter your TOTP code. To disable, click “Disable 2-Factor Auth.”
        </p>
      </details>
    </section>

    <section id="lead-generation" className="hc-section">
      <h2>Lead Generation</h2>
      <details>
        <summary>How do I find business leads?</summary>
        <ol>
          <li>Navigate to Business Finder.</li>
          <li>Enter your industry and location filters.</li>
          <li>Click “Generate” to fetch matching companies.</li>
          <li>Review results and export as CSV or save to a list.</li>
        </ol>
      </details>
      <details>
        <summary>How do I find people leads?</summary>
        <ol>
          <li>Go to People Finder.</li>
          <li>Specify job title and location.</li>
          <li>Click “Generate” to get profiles.</li>
          <li>Download or store them in your “My Lists” page.</li>
        </ol>
      </details>
      <details>
        <summary>What happens if I run out of credits?</summary>
        <p>
          You can either wait for next billing cycle (credits refresh) or upgrade your plan at any time via the Pricing page or Account → Subscription.
        </p>
      </details>
    </section>

    <section id="billing" className="hc-section">
      <h2>Invoices & Billing</h2>
      <details>
        <summary>How do I view or download invoices?</summary>
        <p>
          Head to Account → Invoices. You’ll see a table of all past invoices with a “Download PDF” link.
        </p>
      </details>
      <details>
        <summary>How do I change my payment method?</summary>
        <p>
          In Account → Subscription, click “Manage Payment Info” to open Stripe’s billing portal. Add or update cards there.
        </p>
      </details>
      <details>
        <summary>How do I cancel or downgrade?</summary>
        <p>
          Click “Cancel Subscription” under Account → Subscription. You’ll retain access until period end, after which you’ll revert to the free Trial plan.
        </p>
      </details>
    </section>

    <section id="security" className="hc-section">
      <h2>Security & MFA</h2>
      <details>
        <summary>What is two-factor authentication (MFA)?</summary>
        <p>
          MFA adds an extra code (from an Authenticator app) on top of your password for improved security.
        </p>
      </details>
      <details>
        <summary>How do I set up MFA?</summary>
        <ol>
          <li>Install an authenticator app (Google Authenticator, Authy, etc.).</li>
          <li>In Account → Security click “Enable 2-Factor Auth.”</li>
          <li>Scan the QR code and enter the generated code.</li>
        </ol>
      </details>
    </section>

    <section id="faqs" className="hc-section">
      <h2>Troubleshooting & FAQs</h2>
      <details>
        <summary>My search is returning zero results—what now?</summary>
        <p>
          Check your filters (industry/location). Try broadening the location radius or removing uncommon job titles.
        </p>
      </details>
      <details>
        <summary>I’m not receiving the password reset email</summary>
        <p>
          Check your spam folder. Ensure you’re entering the same email you signed up with. Contact support if issues persist.
        </p>
      </details>
      <details>
        <summary>Can I integrate with my CRM?</summary>
        <p>
          Yes—export CSVs or use our API (coming soon) to push leads directly into Salesforce, HubSpot, or Pipedrive.
        </p>
      </details>
    </section>

    <footer className="hc-footer">
      <p>Need more help? <Link to="/contact-support">Contact Support</Link> or <Link to="/report-bug">Report a Bug</Link>.</p>
    </footer>
  </div>
);

export default HelpCenter;
